import { childNodesOfType, leavesOfType } from 'components/utility/TreeHelpers'
import { LeafTypes, NavigableNodeTypes, NodeType } from 'shared/types/ingestion'
import { INode } from 'shared/types/tree'
import { KiddomStandard } from 'components/standards/types'
import {
  createSpreadsheet,
  appendRowsToSpreadsheet,
  setSpreadsheetWrapStrategy,
  WrapStrategy,
} from 'infrastructure/googleDrive'
import { directTreeAccessUrl } from 'infrastructure/api'
import { createIdMap } from 'components/version_mapping/utility'
import { closestNavigableParent } from 'components/utility/TreeNavigation'
import { stripHTML } from './utility'

export interface SkillsListWithStandards {
  node_id: string
  title: string
  standards: KiddomStandard[]
}

/**
 * Clears background color from all rows in a spreadsheet
 */
async function clearAllRowColors(
  accessToken: string,
  spreadsheetId: string,
  rowCount: number,
  columnCount: number,
): Promise<Response> {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`

  const requestBody = {
    requests: [
      {
        repeatCell: {
          range: {
            sheetId: 0,
            startRowIndex: 0,
            endRowIndex: rowCount,
            startColumnIndex: 0,
            endColumnIndex: columnCount,
          },
          cell: {
            userEnteredFormat: {
              backgroundColor: { red: 1, green: 1, blue: 1 }, // White (default)
            },
          },
          fields: 'userEnteredFormat.backgroundColor',
        },
      },
    ],
  }

  return fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })
}

/**
 * Colors specific rows in a spreadsheet
 */
async function colorSpreadsheetRows(
  accessToken: string,
  spreadsheetId: string,
  rowIndices: number[],
  color: { red: number; green: number; blue: number },
): Promise<Response> {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`

  // Get the number of columns from the sheet
  const getSheetUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets.properties.gridProperties`
  const sheetResponse = await fetch(getSheetUrl, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!sheetResponse.ok) {
    throw new Error(`Error getting sheet info: ${sheetResponse.statusText}`)
  }

  const sheetData = await sheetResponse.json()
  const columnCount =
    sheetData.sheets?.[0]?.properties?.gridProperties?.columnCount || 20

  // Create a request for each row to color
  const colorRequests = rowIndices.map((rowIndex) => {
    return {
      repeatCell: {
        range: {
          sheetId: 0,
          startRowIndex: rowIndex,
          endRowIndex: rowIndex + 1,
          startColumnIndex: 0,
          endColumnIndex: columnCount,
        },
        cell: {
          userEnteredFormat: {
            backgroundColor: color,
          },
        },
        fields: 'userEnteredFormat.backgroundColor',
      },
    }
  })

  const requestBody = {
    requests: colorRequests,
  }

  return fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })
}

/**
 * Formats the header row (row 0) to be bold and italic
 */
async function formatHeaderRow(
  accessToken: string,
  spreadsheetId: string,
  columnCount: number,
): Promise<Response> {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`

  const requestBody = {
    requests: [
      {
        repeatCell: {
          range: {
            sheetId: 0,
            startRowIndex: 0,
            endRowIndex: 1,
            startColumnIndex: 0,
            endColumnIndex: columnCount,
          },
          cell: {
            userEnteredFormat: {
              textFormat: {
                bold: true,
                italic: true,
              },
            },
          },
          fields: 'userEnteredFormat.textFormat',
        },
      },
    ],
  }

  return fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })
}

/**
 * Creates rows from tree.
 * For each navigable node, emits a row with type, title, node_id, link to node, and 12 Level columns.
 * Returns rows and row type information for coloring.
 */
function createRowsFromTree(
  tree: INode,
  standardIdMap: Record<number, KiddomStandard>,
  filterSkillsNodesOnly: boolean = false,
): {
  rows: string[][]
  nodeRowIndices: number[]
} {
  const headers: string[] = [
    'type/standard_id',
    'title/state',
    'node id/standard_name',
    'link to node/standard_description',
    'Level 1/subject_name',
    'Level 2/grade_name',
    'Level 3/adoption_date',
    'Level 4',
    'Level 5',
    'Level 6',
    'Level 7',
    'Level 8',
    'Level 9',
    'Level 10',
    'Level 11',
    'Level 12',
  ]
  const rows: string[][] = []
  const nodeRowIndices: number[] = []

  const idMap = createIdMap(tree)

  function recursivelyMakeRows(node: INode, ancestors: string[] = []) {
    if (
      (node.title && NavigableNodeTypes.includes(node.type as NodeType)) ||
      node.type === NodeType.Question
    ) {
      const parent = closestNavigableParent(node, idMap)

      const linkToNode = directTreeAccessUrl({
        treeId: node.tree_id,
        versionId: node.version,
        nodeId: parent.id,
      })

      // get a list of all child skills lists with their associated standards
      const existingStandardLists = childNodesOfType(node, NodeType.SkillsList)

      const skillsListsWithStandards: SkillsListWithStandards[] =
        existingStandardLists.map((skillsListNode) => {
          const skillIDs = leavesOfType(skillsListNode, LeafTypes.Skill)
            .map((s) => s.body.skillID)
            .filter((id): id is number => id !== undefined && id !== null)

          const standards = skillIDs
            .map((id) => standardIdMap[id])
            .filter(
              (standard): standard is KiddomStandard => standard !== undefined,
            )

          return {
            node_id: skillsListNode.id,
            title: skillsListNode.title || '',
            standards,
          }
        })

      const nodeText =
        node.type === NodeType.Question && node.description
          ? stripHTML(node.description).substring(0, 100)
          : node.title || ''

      const newRow = [
        node.type || '',
        nodeText,
        node.id,
        linkToNode,
        ...ancestors.slice(0, 12),
      ]

      const skillRows: string[][] = []
      let skillsFound = false

      // add a single line here for each skills_list
      skillsListsWithStandards.forEach((skillsList) => {
        const skillsListRow = [
          'skills_list',
          skillsList.title,
          skillsList.node_id,
        ]
        skillRows.push(skillsListRow)

        // for each standard emit a new row with the standards information
        skillsList.standards.forEach((standard) => {
          const standardRow = [
            standard.standard_id.toString(),
            standard.state || '(not set)',
            standard.standard_name || '(not set)',
            standard.standard_description || '(not set)',
            standard.subject_name || '(not set)',
            standard.grade_name || '(not set)',
            standard.adoption_date != null
              ? standard.adoption_date.toString()
              : '(not set)',
          ]
          skillsFound = true
          skillRows.push(standardRow)
        })
      })

      // Only emit node and skill rows if filter is off or skills were found
      if (!filterSkillsNodesOnly || skillsFound) {
        rows.push(newRow)
        // Track node row index (1-based, +1 for header row)
        nodeRowIndices.push(rows.length)

        // Push all skill rows
        skillRows.forEach((skillRow) => {
          rows.push(skillRow)
        })
      }
    }

    node.nodes?.forEach((n) => {
      recursivelyMakeRows(n, [...ancestors, node.title || ''])
    })
  }

  recursivelyMakeRows(tree)

  return {
    rows: [headers, ...rows],
    nodeRowIndices,
  }
}

/**
 * Exports tree standards to a Google Sheet.
 * Creates a new spreadsheet every time.
 * Overwrites cells with nodeid, node_type, title, and link.
 *
 * @param tree - The tree node to export
 * @param accessToken - Google OAuth access token
 * @returns Promise resolving to the spreadsheet ID, or null if export fails
 */
export async function exportTreeStandardsToSheet(
  tree: INode,
  accessToken: string,
  standardIdMap: Record<number, KiddomStandard>,
  filterSkillsNodesOnly: boolean = false,
): Promise<string | null> {
  const { rows, nodeRowIndices } = createRowsFromTree(
    tree,
    standardIdMap,
    filterSkillsNodesOnly,
  )
  const headers = rows[0] || []
  const dataRows = rows.slice(1)

  const spreadsheetName = `Export Standards: ${tree.title}`

  // Create new spreadsheet and append data
  const spreadsheetId = await createSpreadsheet(accessToken, spreadsheetName)
  const appendResponse = await appendRowsToSpreadsheet(
    accessToken,
    spreadsheetId,
    [headers, ...dataRows],
  )
  if (!appendResponse.ok) {
    throw new Error(
      `Failed to append to spreadsheet: ${appendResponse.statusText}`,
    )
  }

  const wrapResponse = await setSpreadsheetWrapStrategy(
    accessToken,
    spreadsheetId,
    WrapStrategy.CLIP,
  )
  if (!wrapResponse.ok) {
    throw new Error(
      `Failed to set spreadsheet wrap strategy: ${wrapResponse.statusText}`,
    )
  }

  // Format header row to be bold and italic
  const formatResponse = await formatHeaderRow(
    accessToken,
    spreadsheetId,
    headers.length,
  )
  if (!formatResponse.ok) {
    throw new Error(`Failed to format header row: ${formatResponse.statusText}`)
  }

  // Get sheet dimensions to clear all row colors
  const getSheetUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets.properties.gridProperties`
  const sheetResponse = await fetch(getSheetUrl, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (sheetResponse.ok) {
    const sheetData = await sheetResponse.json()
    const rowCount =
      sheetData.sheets?.[0]?.properties?.gridProperties?.rowCount || rows.length
    const columnCount =
      sheetData.sheets?.[0]?.properties?.gridProperties?.columnCount ||
      headers.length

    // Clear all row colors first (set to white/default)
    await clearAllRowColors(accessToken, spreadsheetId, rowCount, columnCount)
  }

  // Color node rows with a very faint color to make skills_list rows stand out
  if (nodeRowIndices.length > 0) {
    await colorSpreadsheetRows(
      accessToken,
      spreadsheetId,
      nodeRowIndices,
      { red: 0.94, green: 0.96, blue: 0.98 }, // Light blue (heavier than before, lighter than skills_list was)
    )
  }

  return spreadsheetId
}
